<template>
  <section class="team-section py-5">
    <div class="container">

      <!-- عنوان -->
      <div class="section-heading text-center mb-4 mb-lg-5">
        <span class="heading-badge">{{ currentLang.ourLovelyTeam }}</span>

        <h2 class="heading-title mt-3">
          {{ currentLang.meetOurTeamTitle }}
        </h2>

        <p class="heading-text">
          {{ currentLang.meetOurTeamDescription }}
        </p>
      </div>

      <!-- اسلایدر -->
      <div class="slider-wrapper position-relative">

        <button
          class="slider-button slider-button-right d-none d-md-flex"
          @click="previousSlide"
          :disabled="currentIndex === 0"
          :aria-label="currentLang.previousSlide"
        >
          ❯
        </button>

        <div
          class="slider-viewport"
          @touchstart="touchStart"
          @touchend="touchEnd"
        >
          <div
            class="slider-track d-flex"
            :style="trackStyle"
          >
            <div
              v-for="member in members"
              :key="member.id"
              class="slide-item"
            >
              <article class="member-card">

                <!-- بخش عکس -->
                <div
                  class="member-image-box"
                  :style="{ background: member.background }"
                >
                  <div class="decor decor-one">✦</div>
                  <div class="decor decor-two">●</div>

                  <img
                    :src="member.image"
                    :alt="member.name"
                    class="member-image"
                  />

                  <div class="image-glass-badge">
                    {{ member.emoji }}
                  </div>
                </div>

                <!-- محتوای شیشه‌ای -->
                <div class="member-content">
                   <h3 class="member-name">
                    {{ member.name }}
                  </h3>

                  <span class="member-role">
                    {{ member.role }}
                  </span>

                  <p class="member-description">
                    {{ member.description }}
                  </p> 

                  <div class="member-tags">
                    <span
                      v-for="tag in member.tags"
                      :key="tag"
                      class="member-tag"
                    >
                      {{ tag }}
                    </span>
                  </div> 
                </div>

              </article>
            </div>
          </div>
        </div>

        <button
          class="slider-button slider-button-left d-none d-md-flex"
          @click="nextSlide"
          :disabled="currentIndex >= maxIndex"
          :aria-label="currentLang.nextSlide"
        >
          ❮
        </button>
      </div>

      <!-- نقاط اسلایدر -->
      <div class="slider-dots d-flex justify-content-center gap-2 mt-4">
        <button
          v-for="dot in totalDots"
          :key="dot"
          class="slider-dot"
          :class="{ active: currentIndex === dot - 1 }"
          @click="goToSlide(dot - 1)"
          :aria-label="`${currentLang.goToSlide} ${dot}`"
        ></button>
      </div>

    </div>
  </section>
</template>


<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";


import { lang } from "@/plugins/lang";
import enAuth from "@/locales/en/ourTeamSlider";
import faAuth from "@/locales/fa/ourTeamSlider";

const translations = {
  en: enAuth,
  fa: faAuth,
};
const currentLang = computed(() => translations[lang.value]);
const membersFa = [
  {
    id: 1,
    name: "سارا احمدی",
    role: "مربی زبان کودک",
    description: "سارا با قصه، شعر و بازی‌های سرگرم‌کننده به بچه‌ها زبان انگلیسی یاد می‌دهد.",
    image: "https://i.pravatar.cc/600?img=47",
    emoji: "🎨",
    background: "linear-gradient(145deg, #ffb7d5, #ffdca8)",
    tags: ["قصه‌گویی", "بازی", "زبان"],
  },
  {
    id: 2,
    name: "علی رضایی",
    role: "طراح بازی آموزشی",
    description: "علی بازی‌ها و فعالیت‌های خلاقانه‌ای طراحی می‌کند که یادگیری را شیرین‌تر می‌کند.",
    image: "https://i.pravatar.cc/600?img=12",
    emoji: "🚀",
    background: "linear-gradient(145deg, #9edcff, #c7f5e8)",
    tags: ["خلاقیت", "پازل", "بازی"],
  },
  {
    id: 3,
    name: "مینا کریمی",
    role: "مربی هنر و نقاشی",
    description: "مینا با رنگ‌های شاد و کاردستی‌های جذاب، دنیای خیال بچه‌ها را پرورش می‌دهد.",
    image: "https://i.pravatar.cc/600?img=44",
    emoji: "🖌️",
    background: "linear-gradient(145deg, #d9b8ff, #ffc4dd)",
    tags: ["نقاشی", "هنر", "کاردستی"],
  },
  {
    id: 4,
    name: "مهدی موسوی",
    role: "تولیدکننده محتوای کودک",
    description: "مهدی ویدیوهای شاد و آموزشی تولید می‌کند تا بچه‌ها با علاقه یاد بگیرند.",
    image: "https://i.pravatar.cc/600?img=33",
    emoji: "🎬",
    background: "linear-gradient(145deg, #ffe28c, #ffb28c)",
    tags: ["ویدیو", "انیمیشن", "آموزش"],
  },
];

const membersEn = [
  {
    id: 1,
    name: "Sara Ahmadi",
    role: "Children's Language Coach",
    description: "Sara teaches English to children through stories, songs, and fun games.",
    image: "https://i.pravatar.cc/600?img=47",
    emoji: "🎨",
    background: "linear-gradient(145deg, #ffb7d5, #ffdca8)",
    tags: ["Storytelling", "Games", "Language"],
  },
  {
    id: 2,
    name: "Ali Rezaei",
    role: "Educational Game Designer",
    description: "Ali designs creative games and activities that make learning much sweeter.",
    image: "https://i.pravatar.cc/600?img=12",
    emoji: "🚀",
    background: "linear-gradient(145deg, #9edcff, #c7f5e8)",
    tags: ["Creativity", "Puzzles", "Games"],
  },
  {
    id: 3,
    name: "Mina Karimi",
    role: "Art & Painting Instructor",
    description: "Mina cultivates children's imagination with happy colors and engaging crafts.",
    image: "https://i.pravatar.cc/600?img=44",
    emoji: "🖌️",
    background: "linear-gradient(145deg, #d9b8ff, #ffc4dd)",
    tags: ["Painting", "Art", "Crafts"],
  },
  {
    id: 4,
    name: "Mehdi Mousavi",
    role: "Children's Content Creator",
    description: "Mehdi produces fun and educational videos so kids learn with enthusiasm.",
    image: "https://i.pravatar.cc/600?img=33",
    emoji: "🎬",
    background: "linear-gradient(145deg, #ffe28c, #ffb28c)",
    tags: ["Video", "Animation", "Education"],
  },
];

// در اسکریپت کامپوننت
const members = computed(() => {
  return lang.value === 'fa' ? membersFa : membersEn;
});




const currentIndex = ref(0);
const visibleItems = ref(1);
const startX = ref(0);

const updateVisibleItems = () => {
  const width = window.innerWidth;

  if (width >= 992) {
    visibleItems.value = 3;
  } else if (width >= 768) {
    visibleItems.value = 2;
  } else {
    visibleItems.value = 1;
  }

  if (currentIndex.value > maxIndex.value) {
    currentIndex.value = maxIndex.value;
  }
};

const maxIndex = computed(() => {
  return Math.max(0, members.value.length - visibleItems.value);
});

const totalDots = computed(() => {
  return maxIndex.value + 1;
});

const trackStyle = computed(() => {
  return {
    transform: `translateX(${currentIndex.value * (100 / visibleItems.value)}%)`,
  };
});

const nextSlide = () => {
  if (currentIndex.value < maxIndex.value) {
    currentIndex.value++;
  }
};

const previousSlide = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
  }
};


const goToSlide = (index) => {
  currentIndex.value = Math.min(index, maxIndex.value);
};

const touchStart = (event) => {
  startX.value = event.touches[0].clientX;
};

const touchEnd = (event) => {
  const endX = event.changedTouches[0].clientX;
  const distance = startX.value - endX;

  if (Math.abs(distance) < 50) return;

  if (distance > 0) {
    nextSlide();
  } else {
    previousSlide();
  }
};

onMounted(() => {
  updateVisibleItems();
  window.addEventListener("resize", updateVisibleItems);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateVisibleItems);
});
</script>

<style scoped>
.team-section {
  direction: rtl;
  overflow: hidden;
  
}

@media (max-width: 767.98px) {
  .team-section {
    margin-top: 100px;
  
  }


    .member-content {
    padding: 16px 8px 18px !important;
  }

  .member-name {
    font-size: 1.1rem !important;
  }

  .member-role {
    margin: 8px auto 14px !important;
    font-size: 0.78rem !important;
  }

  .member-description {
    font-size: 0.82rem !important;
    line-height: 1.75 !important;
    margin-bottom: 14px !important;
  }

  .member-tags {
    gap: 4px !important;
  }

  .member-tag {
    padding: 3px 8px !important;
    font-size: 0.7rem !important;
  }



}


/* عنوان */
.heading-badgee {
  display: inline-block;
  padding: 9px 20px;
  color: #fff;
  background: linear-gradient(135deg, #ff7eb3, #ffb347);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 50px;
  box-shadow: 0 8px 20px rgba(103, 65, 165, 0.12);
  backdrop-filter: blur(12px);
  font-weight: 700;
}

.heading-title {
  color: #18325b;
  font-size: 2rem;
  font-weight: 900;
}

.heading-text {
  max-width: 600px;
  margin: 12px auto 0;
  color: #77839a;
  line-height: 2;
}

/* اسلایدر */
.slider-viewport {
  overflow: hidden;
  padding: 20px 8px 30px;
}

.slider-track {
  width: 100%;
  transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-item {
  flex: 0 0 100%;
  padding: 0 10px;
}

@media (min-width: 768px) {
  .slide-item {
    flex-basis: 50%;
  }
}

@media (min-width: 992px) {
  .slide-item {
    flex-basis: 33.333333%;
  }
}

/* کارت شیشه‌ای */
.member-card {
  position: relative;
  height: 100%;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.38);
  box-shadow:
0.15 22px 45px rgba(62, 77, 112, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
}

.member-card:hover {
  transform: translateY(-10px);
  box-shadow:
    0 30px 55px rgba(62, 77, 112, 0.23),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
}

/* عکس بزرگ */
.member-image-box {
  position: relative;
  height: 330px;
  overflow: hidden;
  border-radius: 29px 29px 0 0;
}

.member-image-box::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(255, 255, 255, 0.2),
    transparent 45%
  );
}

.member-image {
  position: absolute;
  bottom: -10px;
  left: 50%;
  z-index: 1;
  width: 88%;
  height: 105%;
  object-fit: cover;
  object-position: center top;
  border-radius: 50% 50% 20px 20px;
  transform: translateX(-50%);
  filter: drop-shadow(0 15px 15px rgba(61, 57, 96, 0.2));
  transition: transform 0.4s ease;
}

.member-card:hover .member-image {
  transform: translateX(-50%) scale(1.06);
}

.image-glass-badge {
  position: absolute;
  right: 20px;
  bottom: 18px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(10px);
  font-size: 1.5rem;
}

.decor {
  position: absolute;
  z-index: 2;
  color: rgba(255, 255, 255, 0.85);
  font-size: 2rem;
}

.decor-one {
  top: 20px;
  right: 25px;
}

.decor-two {
  bottom: 35px;
  left: 25px;
  font-size: 1rem;
}

/* محتوای کارت */



/* ==========================================================================
   استایل ضد سرریز و ریسپانسیو محتوای کارت (member-content)
   ========================================================================== */


   /* ==========================================================================
   استایل قطعی و ضدسرریز ۱۰۰٪ برای member-content
   ========================================================================== */

.member-content {
  box-sizing: border-box !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  padding: 20px 14px 22px;
  text-align: center;
  overflow: hidden !important;
  display: block; /* جلوگیری از رفتار ناخواسته Flexbox در والد */
}

/* نام */
.member-name {
  box-sizing: border-box !important;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 auto 6px !important;
  color: #18325b;
  font-size: 1.25rem;
  font-weight: 900;
  line-height: 1.4;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
}

/* سمت / نقش */
.member-role {
  box-sizing: border-box !important;
  display: inline-block !important;
  max-width: 95% !important;
  margin: 12px auto 18px !important;
  padding: 6px 14px;
  background: #6cceff;
  color: #18324b;
  font-size: 0.82rem;
  font-weight: 700;
  border-radius: 50px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 12px rgba(80, 70, 130, 0.08);
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
}

/* توضیحات */
.member-description {
  box-sizing: border-box !important;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 auto 16px !important;
  color: #68748b;
  font-size: 0.88rem;
  line-height: 1.85;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
}

/* تگ‌ها */
.member-tags {
  box-sizing: border-box !important;
  display: flex !important;
  flex-wrap: wrap !important;
  justify-content: center !important;
  align-items: center !important;
  gap: 6px !important;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 auto !important;
  padding: 0 !important;
}

.member-tag {
  box-sizing: border-box !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  max-width: 100% !important;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50px;
  color: #69748b;
  font-size: 0.72rem;
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
}



/* دکمه‌ها */
.slider-button {
  position: absolute;
  top: 48%;
  z-index: 5;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 12px 25px rgba(50, 70, 100, 0.16);
  backdrop-filter: blur(12px);
  color: #6955a5;
  font-size: 1.4rem;
  transition: 0.3s ease;
}

.slider-button:hover:not(:disabled) {
  background: #ffffff;
  transform: translateY(-50%) scale(1.1);
}

.slider-button:disabled {
  cursor: not-allowed;
  opacity: 0.35;
}

.slider-button-right {
  right: -18px;
}

.slider-button-left {
  left: -18px;
}

/* نقاط */
.slider-dot {
  width: 11px;
  height: 11px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #cdd5e4;
  transition: 0.3s ease;
}

.slider-dot.active {
  width: 30px;
  border-radius: 20px;
  background: linear-gradient(90deg, #ff9ec4, #a994f9);
}

@media (max-width: 767px) {
  .heading-title {
    font-size: 1.55rem;
  }

  .heading-text {
    font-size: 0.9rem;
  }

  .member-image-box {
    height: 350px;
  }

  .member-description {
    min-height: auto;
  }
}
</style>
